<template>
  <div class="tourHotelDepartureDate_datePicker">
    <!-- <div style="border: 1px dashed; padding: 10px; margin: 10px;">Data: {{ months }}</div> -->

    <div class="tourHotel_departureDate hasDatepicker">
      <div
        class="ui-datepicker-inline ui-datepicker ui-widget ui-widget-content ui-helper-clearfix ui-corner-all ui-datepicker-multi ui-datepicker-multi-2"
        style="display: block; width: 34em;"
      >
        <div
          v-for="(m, mIdx) in showMonths"
          :key="m"
          class="ui-datepicker-group"
          :class="
            mIdx === 0
              ? 'ui-datepicker-group-first'
              : 'ui-datepicker-group-last'
          "
        >
          <div
            class="ui-datepicker-header ui-widget-header ui-helper-clearfix ui-corner-left"
          >
            <a
              v-if="mIdx === 0 && hasPreviousMonth"
              class="ui-datepicker-prev ui-corner-all"
              title="< 前の月へ"
              @click.prevent.stop="renderPreviousMonth"
              ><span class="ui-icon ui-icon-circle-triangle-w"
                >&lt; 前の月へ</span
              ></a
            >
            <a
              v-else-if="hasNextMonth"
              class="ui-datepicker-next ui-corner-all"
              title="次の月へ >"
              @click.prevent.stop="renderNextMonth"
              ><span class="ui-icon ui-icon-circle-triangle-e"
                >次の月へ &gt;</span
              ></a
            >
            <a
              v-if="mIdx === 0 && hasPreviousMonth && hasNextMonth"
              class="ui-datepicker-next ui-corner-all ui-datepicker-next-mobileOnly"
              title="次の月へ >"
              @click.prevent.stop="renderNextMonth"
              ><span class="ui-icon ui-icon-circle-triangle-e"
                >次の月へ &gt;</span
              ></a
            >

            <div class="ui-datepicker-title">
              <span class="ui-datepicker-year">{{ months[mIdx].year }}</span
              >{{ i18n.year }}&nbsp;<span class="ui-datepicker-month"
                >{{ months[mIdx].month }}{{ i18n.month }}</span
              >
              <tk-button
                v-if="allowUndecidedDate"
                class="ui-datepicker-date-undecided"
                @click="selectMonth(months[mIdx])"
              >
                {{ months[mIdx].month }}{{ i18n.month }}未定
              </tk-button>
            </div>
          </div>
          <table class="ui-datepicker-calendar">
            <thead>
              <tr>
                <th
                  v-for="(dn, dnIdx) in i18n.dayNames"
                  :key="dnIdx"
                  :class="
                    dnIdx === 0 || dnIdx === 6 ? 'ui-datepicker-week-end' : ''
                  "
                >
                  <span :title="dn + '曜日'">{{ dn }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="w in getWeekIndices(months[mIdx].days)" :key="w">
                <day
                  v-for="(d, idx) in months[mIdx].days.slice(w * 7, w * 7 + 7)"
                  :key="
                    idx +
                    w * 7 +
                    '-' +
                    months[mIdx].month +
                    '-' +
                    months[mIdx].year
                  "
                  :day-number="d.dayNumber"
                  :date="d.date"
                  :is-weekend="d.isWeekend"
                  :starting-date="startingDate"
                  :ending-date="endingDate"
                  :min-enable-date="minEnableDate"
                  :max-enable-date="maxEnableDate"
                  :hovering-date="hoveringDate"
                  :range="range"
                  @mouseover="hoveringDate = d.date"
                  @day-clicked="handleDayClick($event)"
                >
                </day>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="ui-datepicker-row-break"></div>
      </div>
    </div>

    <div class="tourHotelDepartureDate_toolbar">
      <div class="d-flex flex-row">
        <div class="btn tourHotel_button">
          <tk-icon name="calendar" :alt="i18n.departureLabel"></tk-icon>
          <div class="tourHotel_buttonLabel">
            <small>{{ i18n.departureLabel }}</small>
            <span>{{ displayStartingDate }}</span>
          </div>
        </div>

        <div
          v-if="showNightsSelector"
          ref="nightsPulldown"
          class="ui-datepicker-nightsPulldown"
        >
          <button
            type="button"
            class="ui-datepicker-nightsPulldown_trigger"
            @click="isNightsMenuOpen = !isNightsMenuOpen"
          >
            <span>{{ selectedNights !== null ? `${selectedNights}日間` : '指定なし' }}</span>
            <tk-icon name="arrow_left" class="ui-datepicker-nightsPulldown_icon" />
          </button>
          <ul
            v-if="isNightsMenuOpen"
            class="ui-datepicker-nightsPulldown_list"
          >
            <li
              :class="{ '-selected': selectedNights === null }"
              @click="selectNights(null)"
            >
              指定なし
            </li>
            <li
              v-for="n in selectableNights"
              :key="n"
              :class="{ '-selected': selectedNights === n }"
              @click="selectNights(n)"
            >
              {{ n }}日間
            </li>
          </ul>
        </div>
        <div
          v-else
          class="btn tourHotel_button js-only-roundtrip"
          :disabled="!range"
        >
          <tk-icon name="calendar" :alt="i18n.arrivalLabel"></tk-icon>
          <div class="tourHotel_buttonLabel">
            <small>{{ i18n.arrivalLabel }}</small>
            <span>{{ displayEndingDate }}</span>
          </div>
        </div>
      </div>

      <tk-button class="tourHotel_button-red" @click.prevent.stop="onConfirmed"
        >日付を決定する</tk-button
      >
    </div>
  </div>
</template>

<script>
import throttle from 'lodash.throttle'
import format from 'date-fns/format'
import getDay from 'date-fns/getDay'
import addDays from 'date-fns/addDays'
import addYears from 'date-fns/addYears'
import compareAsc from 'date-fns/compareAsc'
import parse from 'date-fns/parse'
import parseISO from 'date-fns/parseISO'
import addMonths from 'date-fns/addMonths'
import getDaysInMonth from 'date-fns/getDaysInMonth'
import isWeekend from 'date-fns/isWeekend'
import startOfToday from 'date-fns/startOfToday'
import startOfTomorrow from 'date-fns/startOfTomorrow'
import startOfDay from 'date-fns/startOfDay'
import TkIcon from '../TkIcon/TkIcon.vue'
import TkButton from '../TkButton/TkButton.vue'
import Day from './Day.vue'
const defaultI18n = {
  dayNames: ['日', '月', '火', '水', '木', '金', '土'],
  year: '年',
  month: '月',
  departureLabel: '出発日',
  arrivalLabel: '帰り出発日',
}
const INVALID_DAY = -1
const MAX_HOURS_TODAY = 14
const SELECT_STARTING_TURN = 0
const SELECT_ENDING_TURN = 1
const getMinEnableDate = () => {
  const now = new Date()
  if (now.getHours() < MAX_HOURS_TODAY) {
    return startOfToday()
  } else {
    return startOfTomorrow()
  }
}

export default {
  name: 'TkDatePicker',
  components: {
    TkIcon,
    Day,
    TkButton,
  },
  props: {
    range: {
      // single, range
      type: Boolean,
      default: false,
    },
    startDate: {
      default: null,
      type: [String, Date],
    },
    endDate: {
      default: null,
      type: [String, Date],
    },
    // Disable calendar before this.
    minDate: {
      default: () => {
        return getMinEnableDate()
      },
      type: [String, Date],
    },
    // Disable calendar after this, default 1 year
    maxDate: {
      default: () => {
        return addYears(new Date(), 1)
      },
      type: [Date, String, Number],
    },
    showMonths: {
      default: 2,
      type: Number,
    },
    i18n: {
      default: () => defaultI18n,
      type: Object,
    },
    ymdFormat: {
      type: String,
      // https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
      default: 'yyyy/MM/dd',
    },
    // 日付未指定選択を許可するかどうか
    allowUndecidedDate: {
      type: Boolean,
      default: false,
    },
    // range が false の場合に、帰り側の枠を「泊数選択」として使えるようにする選択肢
    // 指定しない（空配列）場合は従来通りグレーアウト表示のまま
    selectableNights: {
      type: Array, // of integer
      default: () => [],
    },
    // 現在選択されている泊数
    nights: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      hoveringDate: null,
      startingDate: null,
      endingDate: null,
      startingYearMonth: null,
      selectedNights: null,
      isNightsMenuOpen: false,
      months: [],
      years: [],
      weeksPerMonth: [...Array(5).keys()],
      currentDate: startOfToday(),
      selectingTurn: null,
    }
  },
  computed: {
    minEnableDate() {
      const md = this.parseTringDate(this.minDate)
      const selectableDate = getMinEnableDate()
      // check whether minDate is after 'today'
      return compareAsc(md, selectableDate) === 1
        ? startOfDay(md)
        : selectableDate
    },
    maxEnableDate() {
      return compareAsc(this.maxDate, this.minEnableDate === 1)
        ? this.minEnableDate
        : startOfDay(this.maxDate)
    },
    displayStartingDate() {
      return this.startingDate
        ? format(this.startingDate, this.ymdFormat)
        : this.startingYearMonth
        ? this.startingYearMonth
        : '年/月/日'
    },
    displayEndingDate() {
      return this.endingDate
        ? format(this.endingDate, this.ymdFormat)
        : '年/月/日'
    },
    showNightsSelector() {
      return !this.range && this.selectableNights.length > 0
    },
    hasPreviousMonth() {
      // hasn't before the month of min date
      return (
        this.months[0].year > parseInt(format(this.minEnableDate, 'yyyy')) ||
        this.months[0].month > parseInt(format(this.minEnableDate, 'M'))
      )
    },
    hasNextMonth() {
      const lastActiveMonth = this.months[this.months.length - 1]
      // year of the active month couldn't be after year of the max date
      return (
        lastActiveMonth.year < parseInt(format(this.maxEnableDate, 'yyyy')) ||
        lastActiveMonth.month < parseInt(format(this.maxEnableDate, 'M'))
      )
    },
  },
  watch: {
    startingDate(v) {
      this.$emit('starting-date-changed', v)
    },
    endingDate(v) {
      this.$emit('ending-date-changed', v)
    },
    startingYearMonth(v) {
      this.$emit('starting-year-month-changed', v)
    },
    selectedNights(v) {
      this.$emit('nights-selected', v)
    },
  },
  beforeMount() {
    this.startingDate = this.parseTringDate(this.startDate)
    this.endingDate = this.parseTringDate(this.endDate)
    this.selectedNights = this.nights != null ? Number(this.nights) : null
    this.selectingTurn =
      this.startingDate && !this.endingDate
        ? SELECT_ENDING_TURN
        : SELECT_STARTING_TURN
    const firstDateOfMonth = parseISO(
      format(
        this.startingDate ? this.startingDate : this.minEnableDate,
        'yyyy-MM-01'
      )
    )
    this.generateMonths(firstDateOfMonth)
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    handleOutsideClick(event) {
      if (
        this.isNightsMenuOpen &&
        this.$refs.nightsPulldown &&
        !this.$refs.nightsPulldown.contains(event.target)
      ) {
        this.isNightsMenuOpen = false
      }
    },
    selectNights(n) {
      this.selectedNights = n
      this.isNightsMenuOpen = false
    },
    getWeekIndices(days) {
      // because of getDaysOfMonth always a multiple times of 7
      return [...Array(parseInt(days.length / 7)).keys()]
    },
    parseTringDate(d) {
      if (d === null || d === undefined) {
        return null
      }
      // 日付未指定の場合
      if (typeof d === 'string' && d.length < 10) {
        this.startingYearMonth = d
        return null
      }
      return typeof d === 'string'
        ? parse(d, this.ymdFormat, new Date())
        : startOfDay(d)
    },
    onConfirmed() {
      this.$emit('confirmed', {
        startingDate: this.startingDate,
        endingDate: this.endingDate,
        startingYearMonth: this.startingYearMonth,
        nights: this.selectedNights,
      })
    },
    handleDayClick(event) {
      if (!this.range) {
        this.startingDate = event.date
        return
      }
      if (this.selectingTurn === SELECT_STARTING_TURN) {
        this.selectingTurn = SELECT_ENDING_TURN
        this.startingDate = event.date
        this.endingDate = this.startingDate // clear end by set it to startingDate
      } else {
        this.selectingTurn = SELECT_STARTING_TURN

        // set start and clear end if selecting date before start
        if (event.date < this.startingDate) {
          this.startingDate = event.date
          this.endingDate = this.startingDate
          this.selectingTurn = SELECT_ENDING_TURN
        } else {
          this.endingDate = event.date
        }
      }
    },
    selectMonth(event) {
      this.startingYearMonth = `${event.year}-${String(event.month).padStart(
        2,
        '0'
      )}`
      this.$emit('month-selected', this.startingYearMonth)
      return this.startingYearMonth
    },

    generateMonths(date) {
      this.months = []
      for (let m = 0; m < this.showMonths; m++) {
        this.getDaysOfMonth(addMonths(date, m))
      }
    },

    getDaysOfMonth(firstDateOfMonth) {
      // Day of week (0: sun, 1: mon, ...)
      const firstDayOfMonthInWeek = getDay(firstDateOfMonth)

      const daysOfMonth = []
      // all days before the first day of week is invalid date in calendar
      for (let s = 0; s < firstDayOfMonthInWeek; s++) {
        daysOfMonth.push({ dayNumber: INVALID_DAY })
      }
      // push valid days
      for (let s = 0; s < getDaysInMonth(firstDateOfMonth); s++) {
        const d = addDays(firstDateOfMonth, s)
        daysOfMonth.push({
          dayNumber: s + 1,
          date: d,
          isWeekend: isWeekend(d),
        })
      }
      // fill last week
      const remainDaysInLastWeek = daysOfMonth.length % 7
      if (remainDaysInLastWeek > 0) {
        for (let s = 0; s < 7 - remainDaysInLastWeek; s++) {
          daysOfMonth.push({ dayNumber: INVALID_DAY })
        }
      }

      const daysInMonth = {
        month: parseInt(format(firstDateOfMonth, 'M')),
        year: parseInt(format(firstDateOfMonth, 'yyyy')),
        days: daysOfMonth,
        firstDayOfWeek: firstDayOfMonthInWeek,
      }
      this.months.push(daysInMonth)
    },

    // render next 1 month, that's different with current prod system
    renderNextMonth: throttle(function () {
      const firstActiveMonth = this.months[0]
      // shift 1 month, don't +1 due to Date api month start from 0
      const firstDateOfNextMonth = new Date(
        firstActiveMonth.year,
        firstActiveMonth.month,
        1
      )
      this.generateMonths(firstDateOfNextMonth)

      // fire event
      const lastActiveMonth = this.months[this.months.length - 1]
      this.$emit('next-month', {
        year: lastActiveMonth.year,
        month: lastActiveMonth.month,
      })
    }, 200),

    renderPreviousMonth: throttle(function () {
      const firstActiveMonth = this.months[0]
      // shift << 1 month, Date api month start from 0
      const firstDateOfPreviousMonth = new Date(
        firstActiveMonth.year,
        firstActiveMonth.month - 2,
        1
      )
      this.generateMonths(firstDateOfPreviousMonth)

      this.$emit('previous-month', {
        year: this.months[0].year,
        month: this.months[0].month,
      })
    }, 200),
  },
}
</script>

<style scoped lang="scss">
.ui-datepicker-title {
  position: relative;
  .ui-datepicker-date-undecided {
    border-radius: 5px;
    padding: 5px;
    border: 1px solid;
    background-color: #fff;
    color: #f9423a;
    line-height: 1;
    font-size: 12px;
    cursor: pointer;
    margin-left: 15px;
    margin-top: -3px;
  }

}

.ui-datepicker-next-mobileOnly {
  display: none;
}

.ui-datepicker-nightsPulldown {
  position: relative;
  width: 100%;
  margin-left: 10px;
  align-self: stretch;

  &_trigger {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 0 24px 0 10px;
    border: solid 1px #d2d2d2;
    border-radius: 4px;
    box-sizing: border-box;
    box-shadow: inset 2px 2px 2px 2px rgba(51, 51, 51, 0.1);
    background-color: #fff;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
  }

  &_icon {
    position: absolute;
    top: 50%;
    right: 10px;
    width: 8px;
    transform: translateY(-50%) rotate(-90deg);
  }

  // 開く方向・高さがブラウザの表示位置やサイズで変わらないよう、
  // PC/SP共通で常に下方向、8件分の高さでスクロールさせる
  &_list {
    position: absolute;
    z-index: 20;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    max-height: 256px; // 1件32pxの8件分
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
    background-color: #fff;
    border: solid 1px #d2d2d2;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    li {
      display: flex;
      align-items: center;
      height: 32px;
      box-sizing: border-box;
      padding: 0 10px;
      font-size: 12px;
      white-space: nowrap;
      cursor: pointer;

      &:hover,
      &.-selected {
        background-color: #f2f2f2;
      }
    }
  }
}

// SP のみ、表示する月数を2ヶ月分から1ヶ月分に変更する
// (ページネーションで辿れる総数は showMonths の値のまま変えないため、CSSで2ヶ月目を隠すだけにする)
@media (max-width: 657px) {
  .ui-datepicker-group-last {
    display: none;
  }

  .ui-datepicker-next-mobileOnly {
    display: block;
  }
}
</style>
